import React, { useState, useEffect } from 'react';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { User, School } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  GridLegacy as Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Autocomplete,
} from '@mui/material';

const ALL_ROLES = [
  'superuser', 'admin', 'principal', 'teacher', 'assistant_teacher', 'head_teacher',
  'counselor', 'librarian', 'accountant', 'secretary', 'parent', 'student', 'user'
];
const ROLE_LABELS: Record<string, string> = {
  superuser: 'System Administrator',
  admin: 'School Administrator',
  principal: 'Principal',
  teacher: 'Teacher',
  assistant_teacher: 'Assistant Teacher',
  head_teacher: 'Head Teacher',
  counselor: 'Counselor',
  librarian: 'Librarian',
  accountant: 'Accountant',
  secretary: 'Secretary',
  parent: 'Parent',
  student: 'Student',
  user: 'User',
};

interface NotificationSenderProps {
  token: string;
  onSuccess?: () => void;
}

const NotificationSender: React.FC<NotificationSenderProps> = ({ token, onSuccess }) => {
  const { user } = useAuth();
  const isSuperUser = user?.role === 'superuser';

  // State
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<number | null>(user?.school_id || null);
  const [users, setUsers] = useState<User[]>([]);
  const [role, setRole] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifMsg, setNotifMsg] = useState('');
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSuccess, setNotifSuccess] = useState('');
  const [notifError, setNotifError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Fetch schools for superuser
  useEffect(() => {
    if (isSuperUser) {
      api.get('/schools/').then(res => setSchools(res.data as School[])).catch((err: any) => {
        console.error("Failed to fetch schools:", err);
        setSchools([]);
      });
    }
  }, [isSuperUser]);

  // Fetch users for selected school
  useEffect(() => {
    if (selectedSchool) {
      api.get('/users/', { params: { school_id: selectedSchool } })
        .then(res => setUsers(res.data as User[]))
        .catch((err: any) => {
          console.error("Failed to fetch users:", err);
          setUsers([]);
        });
    }
  }, [selectedSchool]);

  // Fetch notification history (optional: filter by school if superuser)
  useEffect(() => {
    let url = '/announcements/notifications/';
    if (isSuperUser && selectedSchool) {
      url += `?school_id=${selectedSchool}`;
    }
    api.get(url)
      .then(res => setHistory(res.data as any[]))
      .catch((err: any) => {
        console.error("Failed to fetch notification history:", err);
        setHistory([]);
      });
  }, [isSuperUser, selectedSchool, notifSuccess]);

  // Handle send
  const handleSendNotif = (e: React.FormEvent) => {
    e.preventDefault();
    setPreviewOpen(true);
  };

  // Confirm send
  const handleConfirmSend = async () => {
    setNotifLoading(true);
    setNotifSuccess('');
    setNotifError('');
    try {
      let payload: any = {
        title: notifTitle,
        message: notifMsg,
        token,
      };
      if (isSuperUser && selectedSchool) payload.school_id = selectedSchool;
      if (role) payload.role = role;
      if (selectedUsers.length > 0) payload.user_ids = selectedUsers.map(u => u.id);
      await api.post('/announcements/send-push', payload);
      setNotifSuccess('Notification sent!');
      setSnackbarMsg('Notification sent!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setNotifTitle('');
      setNotifMsg('');
      setRole('');
      setSelectedUsers([]);
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setNotifError(err?.response?.data?.detail || err.message || 'Failed to send notification');
      setSnackbarMsg(err?.response?.data?.detail || err.message || 'Failed to send notification');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setNotifLoading(false);
      setPreviewOpen(false);
    }
  };

  // UI helpers
  const availableRoles = isSuperUser ? ALL_ROLES : ALL_ROLES.filter(r => r !== 'superuser');
  const availableUsers = users.filter(u => !role || u.role === role);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <form onSubmit={handleSendNotif}>
        {/* Target Selection Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Target Audience
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isSuperUser && (
              <TextField
                select
                label="School"
                value={selectedSchool || ''}
                onChange={e => setSelectedSchool(Number(e.target.value))}
                required
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3498db',
                    },
                  }
                }}
              >
                <MenuItem value="">Select School</MenuItem>
                {schools.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            )}
            
            <TextField
              select
              label="Role Filter"
              value={role}
              onChange={e => setRole(e.target.value)}
              fullWidth
              size="small"
              helperText="Filter by user role (optional)"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3498db',
                  },
                }
              }}
            >
              <MenuItem value="">All Users</MenuItem>
              {availableRoles.map(r => (
                <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
              ))}
            </TextField>
            
            <Autocomplete
              multiple
              options={availableUsers}
              getOptionLabel={u => u.full_name || u.username || u.email || `User #${u.id}`}
              value={selectedUsers}
              onChange={(_, value) => setSelectedUsers(value)}
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Specific Users (optional)" 
                  placeholder="Search and select individual users..." 
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3498db',
                      },
                    }
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              size="small"
            />
          </Box>
        </Box>

        {/* Notification Content Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Notification Content
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Notification Title"
              value={notifTitle}
              onChange={e => setNotifTitle(e.target.value)}
              required
              fullWidth
              size="small"
              placeholder="Enter a clear, concise title"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3498db',
                  },
                }
              }}
            />
            
            <TextField
              label="Notification Message"
              value={notifMsg}
              onChange={e => setNotifMsg(e.target.value)}
              required
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              size="small"
              placeholder="Enter your notification message here..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3498db',
                  },
                }
              }}
            />
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={notifLoading}
            size="large"
            sx={{ 
              minWidth: 200,
              background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2980b9 0%, #1f5f8b 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {notifLoading ? <CircularProgress size={20} /> : 'Send Notification'}
          </Button>
          
          {notifSuccess && (
            <Typography color="success.main" sx={{ fontWeight: 500 }}>
              {notifSuccess}
            </Typography>
          )}
          {notifError && (
            <Typography color="error.main" sx={{ fontWeight: 500 }}>
              {notifError}
            </Typography>
          )}
        </Box>
      </form>
      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Preview Notification</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography><strong>School:</strong> {isSuperUser ? (schools.find(s => s.id === selectedSchool)?.name || '') : user?.school_name}</Typography>
            <Typography><strong>Role:</strong> {role ? ROLE_LABELS[role] : 'All Users'}</Typography>
            <Typography><strong>Individuals:</strong> {selectedUsers.length > 0 ? selectedUsers.map(u => u.full_name || u.username).join(', ') : 'None'}</Typography>
            <Typography><strong>Title:</strong> {notifTitle}</Typography>
            <Typography><strong>Message:</strong> {notifMsg}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmSend} variant="contained" color="primary" disabled={notifLoading}>
            {notifLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm & Send'}
          </Button>
          <Button onClick={() => setPreviewOpen(false)} color="secondary" variant="outlined">Cancel</Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for feedback */}
      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
      {/* Notification History */}
      <Box mt={5}>
        <Typography variant="h6" fontWeight="bold" mb={2}>Notification History</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 && (
                <TableRow><TableCell colSpan={4} align="center">No notifications sent yet.</TableCell></TableRow>
              )}
              {history.map((n, i) => (
                <TableRow key={i}>
                  <TableCell>{n.title}</TableCell>
                  <TableCell>{n.message}</TableCell>
                  <TableCell>{n.created_at || n.timestamp || ''}</TableCell>
                  <TableCell>{n.status || 'sent'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default NotificationSender; 